import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Checkbox, IconButton } from "@narmi/design_system";
import type Account from "byzantine/src/Account";
import { groupAccountsByType } from "./accountGroups";
import styles from "./AccountFilterPanel.module.scss";

type AccountFilterPanelProps = {
  accounts: Account[];
  selectedAccountIds: string[];
  isOpen: boolean;
  onApply: (ids: string[]) => void;
  onDismiss: () => void;
};

/**
 * Pure filter content: a back button + title, a single "All" checkbox
 * covering every listed account, and per-category groups of individual
 * account checkboxes (no per-category "All"). Has no drawer/trigger chrome
 * of its own — the caller decides how this gets shown (e.g. inside
 * BorderCard's in-card drawer).
 */
const AccountFilterPanel = ({
  accounts,
  selectedAccountIds,
  isOpen,
  onApply,
  onDismiss
}: AccountFilterPanelProps) => {
  const { l10n } = useLocalization();
  const [pendingIds, setPendingIds] = useState<Set<string>>(
    new Set(selectedAccountIds)
  );

  useEffect(() => {
    // Pending selection starts from whatever's currently applied, so
    // reopening the panel doesn't carry over an un-applied change from
    // last time.
    if (isOpen) setPendingIds(new Set(selectedAccountIds));
    // Deliberately excludes selectedAccountIds: only isOpen transitioning
    // should trigger the reset, not every change to what's applied.
  }, [isOpen]);

  const groups = useMemo(() => groupAccountsByType(accounts), [accounts]);

  const selectedCount = accounts.filter((account) => pendingIds.has(account.id))
    .length;
  const allChecked = accounts.length > 0 && selectedCount === accounts.length;
  const isIndeterminate = !allChecked && selectedCount > 0;

  const handleToggleAll = () => {
    setPendingIds(
      allChecked ? new Set() : new Set(accounts.map((account) => account.id))
    );
  };

  const handleToggleAccount = (id: string) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(Array.from(pendingIds));
  };

  return (
    <div className={styles.panel} data-testid="net-worth-filter-panel">
      <div className={styles.header}>
        <IconButton
          name="arrow-left"
          onClick={onDismiss}
          label={l10n.getString("net-worth-filter-back")}
          testId="net-worth-filter-back"
        />
        <span className={styles.title}>
          {l10n.getString("net-worth-filter-title")}
        </span>
      </div>
      <label className={styles.checkboxRow}>
        <Checkbox
          checked={allChecked}
          indeterminate={isIndeterminate}
          onChange={handleToggleAll}
        />
        <span className={styles.allLabel}>
          {l10n.getString("net-worth-filter-select-all")}
        </span>
      </label>
      <div className={styles.groupList}>
        {groups.map((group) => (
          <div key={group.name} className={styles.group}>
            <div className={styles.groupHeader}>{group.name}</div>
            {group.accounts.map((account) => (
              <label key={account.id} className={styles.checkboxRow}>
                <Checkbox
                  checked={pendingIds.has(account.id)}
                  onChange={() => handleToggleAccount(account.id)}
                />
                <span
                  className={styles.checkboxLabel}
                  data-testid="net-worth-filter-account-label"
                >
                  {account.getName()}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <Button onClick={handleApply}>
          {l10n.getString("net-worth-filter-apply")}
        </Button>
      </div>
    </div>
  );
};

export default AccountFilterPanel;
